import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@startupquickstart/frontend';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Controller,
  useForm,
  type Control,
  type FieldErrors,
  type Resolver,
  type UseFormRegister,
} from 'react-hook-form';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCreateTicket,
  type CreateTicketInput,
  type TicketAttachment,
} from '@/api/support';
import { TICKET_SCHEMAS } from '@/api/support-schemas';
import { useBugContext } from '@/hooks/useBugContext';
import { LinearAttachmentInput } from './LinearAttachmentInput';

type TicketType = CreateTicketInput['type'];

interface TicketFormProps {
  type: TicketType;
  onSuccess?: (created: { id: string; identifier: string }) => void;
  onCancel?: () => void;
  /**
   * Initial value for the bug-only URL field. Pass an empty string to
   * suppress the auto-capture (e.g. when opened from the support page
   * itself, where the URL would be misleading).
   */
  initialUrl?: string;
}

const ENTITY_KIND_OPTIONS = [
  'attraction',
  'tour',
  'hotel',
  'neighborhood',
  'greater-area',
] as const;

/**
 * Wide form-values shape that's the UNION of every ticket variant's
 * fields. Per-type fields are optional at the TS level; the active Zod
 * schema enforces which are actually required at runtime. This keeps
 * react-hook-form's `register` happy without disjoint-union acrobatics.
 */
interface FormValues {
  type: TicketType;
  title: string;
  description: string;
  attachments: TicketAttachment[];
  // bug
  area?: string;
  stepsToReproduce?: string;
  expected?: string;
  actual?: string;
  frequency?: 'always' | 'sometimes' | 'once';
  severity?: 'blocker' | 'serious' | 'minor' | 'cosmetic';
  startedAt?: string;
  url?: string;
  // feature
  audience?: 'guest' | 'hotel' | 'internal';
  problem?: string;
  workaround?: string;
  acceptanceCriteria?: string;
  priority?: 'nice-to-have' | 'important' | 'urgent';
  // content-fix + data-quality
  entityKind?: 'attraction' | 'tour' | 'hotel' | 'neighborhood' | 'greater-area';
  entityIdOrName?: string;
  // content-fix
  whatsWrong?: string;
  whatItShouldSay?: string;
  source_link?: string;
  // question
  whatYoureTryingToDo?: string;
  whatYouTried?: string;
  urgency?: 'blocking' | 'soon' | 'whenever';
  // data-quality
  fieldName?: string;
  expectedValue?: string;
  visibility?: 'guest-visible' | 'internal-only';
}

/**
 * Type-driven ticket form using react-hook-form + zod-resolver. The
 * client-side schemas in `@/api/support-schemas` mirror the backend, so
 * the user gets immediate per-field error messages without a network
 * round-trip. Server-side validation errors (if the schemas ever drift)
 * are surfaced from the create mutation.
 */
export function TicketForm({
  type,
  onSuccess,
  onCancel,
  initialUrl,
}: TicketFormProps) {
  const navigate = useNavigate();
  const create = useCreateTicket();
  const bugContext = useBugContext();

  const defaultValues = useMemo(
    () => buildDefaultValues(type, initialUrl ?? bugContext.url),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, initialUrl],
  );

  const form = useForm<FormValues>({
    // The schema for the active type validates exactly its required
    // fields; unused fields on FormValues for other variants are
    // tolerated by the wide form shape but stripped before submit.
    // The cast is necessary because the Zod schema is keyed against the
    // tighter per-variant shape rather than our flat FormValues.
    resolver: zodResolver(TICKET_SCHEMAS[type]) as unknown as Resolver<FormValues>,
    defaultValues,
    mode: 'onTouched',
  });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const attachments = watch('attachments') ?? [];

  async function onSubmit(values: FormValues) {
    try {
      // Strip per-type fields that don't belong to the active variant so
      // we don't send extras to the backend.
      const cleaned = stripForType(values, type);
      const payload = {
        ...cleaned,
        ...(type === 'bug'
          ? {
              userAgent: bugContext.userAgent,
              viewport: bugContext.viewport,
              appVersion: bugContext.appVersion,
            }
          : {}),
        source: 'tours-client' as const,
      } as CreateTicketInput;

      const created = await create.mutateAsync(payload);
      toast.success(`Filed ${created.identifier}`);
      if (onSuccess) {
        onSuccess({ id: created.id, identifier: created.identifier });
      } else {
        navigate(`/internal/support/${created.id}`);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
      <Field label="Title" required error={errors.title?.message}>
        <Input
          {...register('title')}
          placeholder="One-line summary"
        />
      </Field>

      <Field
        label="Description"
        required
        hint="What's going on? Plain language is fine."
        error={errors.description?.message}
      >
        <Textarea
          {...register('description')}
          rows={4}
          placeholder="Describe what you're seeing or what you'd like — a sentence or two is fine."
        />
      </Field>

      {type === 'bug' && (
        <BugFields control={control} register={register} errors={errors} />
      )}
      {type === 'feature' && (
        <FeatureFields control={control} register={register} errors={errors} />
      )}
      {type === 'content-fix' && (
        <ContentFixFields control={control} register={register} errors={errors} />
      )}
      {type === 'question' && (
        <QuestionFields control={control} register={register} errors={errors} />
      )}
      {type === 'data-quality' && (
        <DataQualityFields control={control} register={register} errors={errors} />
      )}

      <Field label="Attachments (optional)">
        <LinearAttachmentInput
          value={attachments}
          onChange={(next) =>
            setValue('attachments', next, { shouldDirty: true })
          }
        />
      </Field>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel ?? (() => navigate('/internal/support'))}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={create.isPending}
          disabled={Object.keys(errors).length > 0}
        >
          File ticket
        </Button>
      </div>
    </form>
  );
}

// ─── Per-type field groups ────────────────────────────────────────────────

interface FieldGroupProps {
  control: Control<FormValues>;
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
}

function BugFields({ control, register, errors }: FieldGroupProps) {
  return (
    <>
      <Field
        label="Area"
        hint="Which page or feature?"
        error={errors.area?.message}
      >
        <Input
          {...register('area')}
          placeholder="e.g. Tours list, Attraction detail"
        />
      </Field>
      <Field
        label="Steps to reproduce"
        error={errors.stepsToReproduce?.message}
      >
        <Textarea
          {...register('stepsToReproduce')}
          rows={3}
          placeholder={'1. Go to …\n2. Click …\n3. See …'}
        />
      </Field>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Expected" error={errors.expected?.message}>
          <Textarea
            {...register('expected')}
            rows={3}
            placeholder="What should have happened?"
          />
        </Field>
        <Field label="Actual" error={errors.actual?.message}>
          <Textarea
            {...register('actual')}
            rows={3}
            placeholder="What actually happened?"
          />
        </Field>
      </div>
      <div className="grid gap-6 sm:grid-cols-3">
        <Field label="Frequency">
          <ControlledSelect
            control={control}
            name="frequency"
            options={['always', 'sometimes', 'once']}
          />
        </Field>
        <Field label="Severity">
          <ControlledSelect
            control={control}
            name="severity"
            options={['blocker', 'serious', 'minor', 'cosmetic']}
          />
        </Field>
        <Field label="Started" error={errors.startedAt?.message}>
          <Input
            {...register('startedAt')}
            placeholder='e.g. "yesterday", "this morning"'
          />
        </Field>
      </div>
      <Field
        label="URL (optional)"
        hint="Auto-filled from the page you were on. Clear it if you're not reporting about a specific page."
        error={errors.url?.message}
      >
        <Input
          type="url"
          {...register('url')}
          placeholder="https://…"
        />
      </Field>
      <p className="text-xs text-muted-foreground">
        We've also captured your browser, viewport, and app version
        automatically — you don't need to fill those in.
      </p>
    </>
  );
}

function FeatureFields({ control, register, errors }: FieldGroupProps) {
  return (
    <>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Who is this for?">
          <ControlledSelect
            control={control}
            name="audience"
            options={['guest', 'hotel', 'internal']}
          />
        </Field>
        <Field label="Priority">
          <ControlledSelect
            control={control}
            name="priority"
            options={['nice-to-have', 'important', 'urgent']}
          />
        </Field>
      </div>
      <Field label="Problem" error={errors.problem?.message}>
        <Textarea
          {...register('problem')}
          rows={3}
          placeholder="What problem would this feature solve?"
        />
      </Field>
      <Field
        label="Acceptance criteria"
        hint="What does done look like?"
        error={errors.acceptanceCriteria?.message}
      >
        <Textarea
          {...register('acceptanceCriteria')}
          rows={3}
          placeholder='e.g. "I can multi-select attractions and edit shared fields in one save."'
        />
      </Field>
      <Field
        label="Current workaround (optional)"
        error={errors.workaround?.message}
      >
        <Textarea
          {...register('workaround')}
          rows={2}
          placeholder="How are you handling this today, if at all?"
        />
      </Field>
    </>
  );
}

function ContentFixFields({ control, register, errors }: FieldGroupProps) {
  return (
    <>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Entity kind">
          <ControlledSelect
            control={control}
            name="entityKind"
            options={[...ENTITY_KIND_OPTIONS]}
          />
        </Field>
        <Field label="Entity ID or name" error={errors.entityIdOrName?.message}>
          <Input
            {...register('entityIdOrName')}
            placeholder="UUID or human-readable name"
          />
        </Field>
      </div>
      <Field label="What's wrong?" error={errors.whatsWrong?.message}>
        <Textarea {...register('whatsWrong')} rows={3} />
      </Field>
      <Field label="What it should say" error={errors.whatItShouldSay?.message}>
        <Textarea {...register('whatItShouldSay')} rows={3} />
      </Field>
      <Field label="Source link (optional)" error={errors.source_link?.message}>
        <Input
          type="url"
          {...register('source_link')}
          placeholder="https://…"
        />
      </Field>
    </>
  );
}

function QuestionFields({ control, register, errors }: FieldGroupProps) {
  return (
    <>
      <Field
        label="What are you trying to do?"
        error={errors.whatYoureTryingToDo?.message}
      >
        <Textarea {...register('whatYoureTryingToDo')} rows={3} />
      </Field>
      <Field
        label="What have you tried? (optional)"
        error={errors.whatYouTried?.message}
      >
        <Textarea {...register('whatYouTried')} rows={3} />
      </Field>
      <Field label="Urgency">
        <ControlledSelect
          control={control}
          name="urgency"
          options={['blocking', 'soon', 'whenever']}
        />
      </Field>
    </>
  );
}

function DataQualityFields({ control, register, errors }: FieldGroupProps) {
  return (
    <>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Entity kind">
          <ControlledSelect
            control={control}
            name="entityKind"
            options={[...ENTITY_KIND_OPTIONS]}
          />
        </Field>
        <Field label="Entity ID or name" error={errors.entityIdOrName?.message}>
          <Input {...register('entityIdOrName')} />
        </Field>
      </div>
      <Field label="Field name" error={errors.fieldName?.message}>
        <Input
          {...register('fieldName')}
          placeholder="e.g. streetAddress, longitude, name"
        />
      </Field>
      <Field label="Expected value" error={errors.expectedValue?.message}>
        <Textarea {...register('expectedValue')} rows={2} />
      </Field>
      <Field label="Visibility">
        <ControlledSelect
          control={control}
          name="visibility"
          options={['guest-visible', 'internal-only']}
        />
      </Field>
    </>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function buildDefaultValues(type: TicketType, capturedUrl: string): FormValues {
  const base: FormValues = {
    type,
    title: '',
    description: '',
    attachments: [],
  };
  switch (type) {
    case 'bug':
      return {
        ...base,
        area: '',
        stepsToReproduce: '',
        expected: '',
        actual: '',
        frequency: 'always',
        severity: 'minor',
        startedAt: '',
        url: capturedUrl,
      };
    case 'feature':
      return {
        ...base,
        audience: 'internal',
        problem: '',
        workaround: '',
        acceptanceCriteria: '',
        priority: 'important',
      };
    case 'content-fix':
      return {
        ...base,
        entityKind: 'attraction',
        entityIdOrName: '',
        whatsWrong: '',
        whatItShouldSay: '',
        source_link: '',
      };
    case 'question':
      return {
        ...base,
        whatYoureTryingToDo: '',
        whatYouTried: '',
        urgency: 'soon',
      };
    case 'data-quality':
      return {
        ...base,
        entityKind: 'attraction',
        entityIdOrName: '',
        fieldName: '',
        expectedValue: '',
        visibility: 'guest-visible',
      };
  }
}

const TYPE_FIELDS: Record<TicketType, ReadonlyArray<keyof FormValues>> = {
  bug: [
    'area',
    'stepsToReproduce',
    'expected',
    'actual',
    'frequency',
    'severity',
    'startedAt',
    'url',
  ],
  feature: [
    'audience',
    'problem',
    'workaround',
    'acceptanceCriteria',
    'priority',
  ],
  'content-fix': [
    'entityKind',
    'entityIdOrName',
    'whatsWrong',
    'whatItShouldSay',
    'source_link',
  ],
  question: ['whatYoureTryingToDo', 'whatYouTried', 'urgency'],
  'data-quality': [
    'entityKind',
    'entityIdOrName',
    'fieldName',
    'expectedValue',
    'visibility',
  ],
};

const COMMON_FIELDS: ReadonlyArray<keyof FormValues> = [
  'type',
  'title',
  'description',
  'attachments',
];

/**
 * Pluck only the fields that belong on the wire for the active ticket
 * type, dropping any per-variant defaults that aren't relevant.
 */
function stripForType(values: FormValues, type: TicketType): FormValues {
  const allowed = new Set<keyof FormValues>([
    ...COMMON_FIELDS,
    ...TYPE_FIELDS[type],
  ]);
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(values) as (keyof FormValues)[]) {
    if (allowed.has(key)) {
      const v = values[key];
      if (v !== undefined && v !== '') {
        out[key] = v;
      } else if (COMMON_FIELDS.includes(key) || key === 'url') {
        // url stays even when empty (caller treats '' as "no url")
        out[key] = v;
      }
    }
  }
  return out as unknown as FormValues;
}

interface ApiErrorBody {
  error?: string;
  details?: Array<{ message?: string; path?: string[] }>;
}

function extractErrorMessage(err: unknown): string {
  if (!err) return 'Could not file ticket';
  const e = err as Error & { response?: { data?: ApiErrorBody } };
  const body =
    e.response?.data ?? safeParseJson<ApiErrorBody>(e.message);
  if (body?.details && body.details.length > 0) {
    const first = body.details[0];
    const path = first?.path?.join('.') ?? 'field';
    const message = first?.message ?? 'invalid value';
    return `${path}: ${message}`;
  }
  if (body?.error) return body.error;
  return e instanceof Error ? e.message : 'Could not file ticket';
}

function safeParseJson<T>(value: string | undefined): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

// ─── UI primitives ───────────────────────────────────────────────────────

function Field({
  label,
  hint,
  required,
  error,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="ml-1 text-destructive">*</span>}
      </Label>
      {children}
      {error ? (
        <p className="text-xs font-medium text-destructive">{error}</p>
      ) : (
        hint && <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

function ControlledSelect({
  control,
  name,
  options,
}: {
  control: Control<FormValues>;
  name: keyof FormValues;
  options: readonly string[];
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Select
          value={(field.value as string | undefined) ?? ''}
          onValueChange={field.onChange}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  );
}
